import { useState, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Gavel, ArrowLeft, Trophy, FileSignature } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Licit = {
  id: string; folio: string; numero_oficial?: string;
  modalidad: string; caracter: string; estado: string; objeto: string;
  fecha_publicacion?: string; fecha_apertura_propuestas?: string; fecha_fallo?: string;
  monto_referencia?: number; monto_adjudicado?: number;
  proveedor_ganador_nombre?: string;
  n_participantes: number; n_propuestas: number;
};

export function LicitacionesPage() {
  const [estado, setEstado] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['licitaciones', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Licit[]>(`/licitaciones?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · Licitaciones</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Licitaciones públicas</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Convocatoria, junta de aclaraciones, apertura de propuestas, dictamen y fallo (LAASSP/LOPSRM).
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}><Plus size={16} /> Nueva convocatoria</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit overflow-x-auto">
        {['', 'convocatoria', 'junta_aclaraciones', 'apertura_propuestas', 'dictamen', 'fallo', 'adjudicada', 'desierta', 'cancelada'].map(e => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('whitespace-nowrap rounded px-3 py-1.5 capitalize',
              estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e ? e.replace(/_/g, ' ') : 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Gavel className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin licitaciones.</p>
            </div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Folio / Nº</th>
                  <th className="px-5 py-3">Modalidad</th>
                  <th className="px-5 py-3">Objeto</th>
                  <th className="px-5 py-3 text-center">Part.</th>
                  <th className="px-5 py-3 text-center">Prop.</th>
                  <th className="px-5 py-3 text-right">Ref / Adjud.</th>
                  <th className="px-5 py-3">Ganador</th>
                  <th className="px-5 py-3">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(l => (
                  <tr key={l.id} className="hover:bg-muted/50">
                    <td className="px-5 py-3">
                      <div className="num text-[12px] font-semibold">{l.folio}</div>
                      <div className="num text-[11px] text-ink-mute">{l.numero_oficial ?? '—'}</div>
                    </td>
                    <td className="px-5 py-3 text-[12px] capitalize">{l.modalidad.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3 max-w-[280px] truncate">{l.objeto}</td>
                    <td className="num px-5 py-3 text-center">{l.n_participantes}</td>
                    <td className="num px-5 py-3 text-center">{l.n_propuestas}</td>
                    <td className="num px-5 py-3 text-right">
                      {l.monto_adjudicado ? (
                        <div>
                          <div className="text-[11px] text-ink-mute">ref {fmt.money(l.monto_referencia ?? 0)}</div>
                          <div className="font-semibold text-emerald-700">{fmt.money(l.monto_adjudicado)}</div>
                        </div>
                      ) : (
                        <span className="text-ink-mute">{fmt.money(l.monto_referencia ?? 0)}</span>
                      )}
                    </td>
                    <td className="px-5 py-3">{l.proveedor_ganador_nombre ?? '—'}</td>
                    <td className="px-5 py-3"><EstadoPill e={l.estado} /></td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/app/licitaciones/${l.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <NuevaLicitacionModal open={openNew} onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); qc.invalidateQueries({ queryKey: ['licitaciones'] }); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    convocatoria: 'bg-stone-100 text-ink-mute',
    junta_aclaraciones: 'bg-sky-50 text-sky-700',
    apertura_propuestas: 'bg-amber-50 text-amber-700',
    dictamen: 'bg-amber-50 text-amber-700',
    fallo: 'bg-brand-50 text-brand-700',
    adjudicada: 'bg-emerald-50 text-emerald-700',
    desierta: 'bg-rose-50 text-rose-700',
    cancelada: 'bg-rose-50 text-rose-700',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', cfg[e] ?? 'bg-muted')}>{e.replace(/_/g, ' ')}</span>;
}

function NuevaLicitacionModal({ open, onClose, onCreated }: any) {
  const [v, setV] = useState({
    numero_oficial: '', modalidad: 'licitacion_publica', caracter: 'nacional',
    objeto: '', fundamento_legal: '', monto_referencia: 0,
    fecha_publicacion: '', fecha_junta: '', fecha_apertura: '', fecha_fallo: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      const toISO = (d: string) => d ? new Date(d).toISOString() : undefined;
      await api('/licitaciones', {
        method: 'POST',
        body: {
          numero_oficial: v.numero_oficial || undefined,
          modalidad: v.modalidad, caracter: v.caracter,
          objeto: v.objeto,
          fundamento_legal: v.fundamento_legal || undefined,
          monto_referencia: Number(v.monto_referencia),
          fecha_publicacion: toISO(v.fecha_publicacion),
          fecha_junta_aclaraciones: toISO(v.fecha_junta),
          fecha_apertura_propuestas: toISO(v.fecha_apertura),
          fecha_fallo: toISO(v.fecha_fallo),
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva convocatoria" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="lic-form" loading={subm}>Publicar convocatoria</Button>
      </>}>
      <form id="lic-form" onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {err && <div className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <Input name="num" label="Número oficial" hint="Ej: LP-2026-001"
          value={v.numero_oficial} onChange={(e) => setV({ ...v, numero_oficial: e.target.value })} />
        <div>
          <label className="text-[13px] font-medium">Modalidad</label>
          <select value={v.modalidad} onChange={(e) => setV({ ...v, modalidad: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="licitacion_publica">Licitación pública</option>
            <option value="invitacion_3">Invitación a 3</option>
            <option value="adjudicacion_directa">Adjudicación directa</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Carácter</label>
          <select value={v.caracter} onChange={(e) => setV({ ...v, caracter: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="nacional">Nacional</option>
            <option value="internacional">Internacional</option>
            <option value="internacional_tlc">Internacional bajo TLC</option>
          </select>
        </div>
        <Input name="fl" label="Fundamento legal"
          value={v.fundamento_legal} onChange={(e) => setV({ ...v, fundamento_legal: e.target.value })} />
        <div className="sm:col-span-2">
          <label className="text-[13px] font-medium">Objeto</label>
          <textarea required rows={2} value={v.objeto} onChange={(e) => setV({ ...v, objeto: e.target.value })}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
        <Input name="mref" label="Monto referencia" type="number" step="0.01"
          value={v.monto_referencia} onChange={(e) => setV({ ...v, monto_referencia: parseFloat(e.target.value) || 0 })} />
        <Input name="pub" label="Publicación" type="date" value={v.fecha_publicacion} onChange={(e) => setV({ ...v, fecha_publicacion: e.target.value })} />
        <Input name="junta" label="Junta aclaraciones" type="date" value={v.fecha_junta} onChange={(e) => setV({ ...v, fecha_junta: e.target.value })} />
        <Input name="apert" label="Apertura propuestas" type="date" value={v.fecha_apertura} onChange={(e) => setV({ ...v, fecha_apertura: e.target.value })} />
        <Input name="fallo" label="Fallo" type="date" value={v.fecha_fallo} onChange={(e) => setV({ ...v, fecha_fallo: e.target.value })} />
      </form>
    </Modal>
  );
}

// =========================================================================
// Detalle
// =========================================================================

export function LicitacionDetallePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [openPart, setOpenPart] = useState(false);
  const [openProp, setOpenProp] = useState<string | null>(null);
  const [openFallo, setOpenFallo] = useState(false);

  const { data: l } = useQuery<any>({ queryKey: ['licitacion', id], queryFn: () => api(`/licitaciones/${id}`) });

  async function registrarActa(endpoint: 'acta-junta' | 'acta-apertura') {
    const texto = prompt(`Acta ${endpoint === 'acta-junta' ? 'junta aclaraciones' : 'apertura'}:`);
    if (!texto) return;
    try {
      await api(`/licitaciones/${id}/${endpoint}`, { method: 'POST', body: { texto } });
      qc.invalidateQueries({ queryKey: ['licitacion', id] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (!l) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/licitaciones" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a licitaciones
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700">{l.folio} · {l.numero_oficial}</p>
            <h1 className="font-display text-[22px] font-bold tracking-tight text-ink line-clamp-2">{l.objeto}</h1>
            <p className="text-[12px] text-ink-mute capitalize">{l.modalidad.replace(/_/g, ' ')} · {l.caracter} · {l.estado.replace(/_/g, ' ')}</p>
          </div>
          <div className="flex gap-2">
            {l.estado === 'convocatoria' && (
              <>
                <Button variant="secondary" size="sm" onClick={() => setOpenPart(true)}><Plus size={12} /> Participante</Button>
                <Button variant="secondary" size="sm" onClick={() => registrarActa('acta-junta')}>Acta junta</Button>
              </>
            )}
            {(l.estado === 'junta_aclaraciones' || l.estado === 'convocatoria') && (
              <Button variant="secondary" size="sm" onClick={() => registrarActa('acta-apertura')}>Acta apertura</Button>
            )}
            {l.estado !== 'adjudicada' && l.estado !== 'desierta' && (
              <Button onClick={() => setOpenFallo(true)}><FileSignature size={14} /> Emitir fallo</Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Monto ref." value={fmt.money(l.monto_referencia ?? 0)} />
        <Stat label="Adjudicado" value={l.monto_adjudicado ? fmt.money(l.monto_adjudicado) : '—'} />
        <Stat label="Participantes" value={(l.participantes ?? []).length.toString()} />
        <Stat label="Propuestas" value={(l.participantes ?? []).filter((p: any) => p.propuesta_id).length.toString()} />
      </div>

      {(l.acta_junta_aclaraciones || l.acta_apertura) && (
        <Card>
          <CardHeader title="Actas" />
          <CardBody className="space-y-3 text-[13px]">
            {l.acta_junta_aclaraciones && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute">Acta junta aclaraciones</div>
                <p className="mt-1">{l.acta_junta_aclaraciones}</p>
              </div>
            )}
            {l.acta_apertura && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute">Acta apertura</div>
                <p className="mt-1">{l.acta_apertura}</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Participantes y propuestas" subtitle={l.criterio_evaluacion ?? 'Criterio: precio'} />
        <CardBody className="p-0">
          {(l.participantes ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin participantes.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Proveedor</th>
                  <th className="px-5 py-3 text-center">Bases</th>
                  <th className="px-5 py-3 text-center">Junta</th>
                  <th className="px-5 py-3 text-right">Monto económico</th>
                  <th className="px-5 py-3 text-right">T. entrega</th>
                  <th className="px-5 py-3 text-center">Cumple</th>
                  <th className="px-5 py-3 text-right">Puntos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {l.participantes.map((p: any) => (
                  <tr key={p.id} className={cn(p.es_ganador && 'bg-emerald-50/60')}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        {p.es_ganador && <Trophy size={14} className="text-emerald-600" />}
                        <span>{p.proveedor_nombre}</span>
                      </div>
                      <div className="num text-[11px] text-ink-mute">{p.proveedor_rfc}</div>
                    </td>
                    <td className="px-5 py-3 text-center">{p.recogio_bases ? '✓' : '—'}</td>
                    <td className="px-5 py-3 text-center">{p.asistio_junta ? '✓' : '—'}</td>
                    <td className="num px-5 py-3 text-right font-semibold">
                      {p.monto_economico != null ? fmt.money(p.monto_economico) : '—'}
                    </td>
                    <td className="num px-5 py-3 text-right">{p.tiempo_entrega_dias ?? '—'}</td>
                    <td className="px-5 py-3 text-center text-[11px]">
                      {p.cumple_tecnica ? 'T' : ''}{p.cumple_legal ? 'L' : ''}
                    </td>
                    <td className="num px-5 py-3 text-right">{p.puntos_totales ?? '—'}</td>
                    <td className="px-5 py-3 text-right">
                      {!p.propuesta_id && l.estado !== 'adjudicada' && (
                        <Button size="sm" variant="ghost" onClick={() => setOpenProp(p.id)}>+ Propuesta</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {(l.dictamen || l.fallo) && (
        <Card>
          <CardHeader title="Dictamen y fallo" />
          <CardBody className="space-y-3 text-[13px]">
            {l.dictamen && <div><div className="text-[11px] font-semibold uppercase text-ink-mute">Dictamen</div><p className="mt-1">{l.dictamen}</p></div>}
            {l.fallo && <div><div className="text-[11px] font-semibold uppercase text-ink-mute">Fallo</div><p className="mt-1">{l.fallo}</p></div>}
          </CardBody>
        </Card>
      )}

      <ParticipanteModal licitID={id} open={openPart} onClose={() => setOpenPart(false)}
        onDone={() => { setOpenPart(false); qc.invalidateQueries({ queryKey: ['licitacion', id] }); }} />
      <PropuestaModal licitID={id} participanteID={openProp} onClose={() => setOpenProp(null)}
        onDone={() => { setOpenProp(null); qc.invalidateQueries({ queryKey: ['licitacion', id] }); }} />
      <FalloModal licitID={id} participantes={l.participantes ?? []} open={openFallo} onClose={() => setOpenFallo(false)}
        onDone={() => { setOpenFallo(false); qc.invalidateQueries({ queryKey: ['licitacion', id] }); }} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[18px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function ParticipanteModal({ licitID, open, onClose, onDone }: any) {
  const [provID, setProvID] = useState('');
  const [recBases, setRecBases] = useState(true);
  const [subm, setSubm] = useState(false);

  const { data: provs } = useQuery({
    queryKey: ['proveedores-pick'], enabled: open,
    queryFn: () => api<{ items: any[] }>('/proveedores?limit=500'),
  });

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/licitaciones/${licitID}/participantes`, {
        method: 'POST',
        body: { proveedor_id: provID, recogio_bases: recBases },
      });
      onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Registrar participante"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="part-form" loading={subm}>Registrar</Button>
      </>}>
      <form id="part-form" onSubmit={submit} className="flex flex-col gap-3">
        <div>
          <label className="text-[13px] font-medium">Proveedor</label>
          <select required value={provID} onChange={(e) => setProvID(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="">(selecciona)</option>
            {(provs?.items ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>)}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={recBases} onChange={(e) => setRecBases(e.target.checked)} />
          Recogió bases
        </label>
      </form>
    </Modal>
  );
}

function PropuestaModal({ licitID, participanteID, onClose, onDone }: any) {
  const [v, setV] = useState({
    monto_economico: 0, tiempo_entrega_dias: 0,
    puntos_tecnica: 0, puntos_economica: 0, puntos_totales: 0,
    cumple_tecnica: true, cumple_legal: true,
  });
  const [subm, setSubm] = useState(false);

  if (!participanteID) return null;

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/licitaciones/${licitID}/propuestas`, {
        method: 'POST',
        body: { participante_id: participanteID, ...v, monto_economico: Number(v.monto_economico) },
      });
      onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={!!participanteID} onClose={onClose} title="Registrar propuesta"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="prop-form" loading={subm}>Guardar</Button>
      </>}>
      <form id="prop-form" onSubmit={submit} className="grid grid-cols-2 gap-3">
        <Input name="me" label="Monto económico" type="number" step="0.01" required
          value={v.monto_economico} onChange={(e) => setV({ ...v, monto_economico: parseFloat(e.target.value) || 0 })} />
        <Input name="te" label="Tiempo entrega (días)" type="number"
          value={v.tiempo_entrega_dias} onChange={(e) => setV({ ...v, tiempo_entrega_dias: parseInt(e.target.value) || 0 })} />
        <Input name="pt" label="Puntos técnica" type="number" step="0.01"
          value={v.puntos_tecnica} onChange={(e) => setV({ ...v, puntos_tecnica: parseFloat(e.target.value) || 0 })} />
        <Input name="pe" label="Puntos económica" type="number" step="0.01"
          value={v.puntos_economica} onChange={(e) => setV({ ...v, puntos_economica: parseFloat(e.target.value) || 0 })} />
        <Input name="px" label="Puntos totales" type="number" step="0.01"
          value={v.puntos_totales} onChange={(e) => setV({ ...v, puntos_totales: parseFloat(e.target.value) || 0 })} />
        <div className="col-span-2 flex gap-4 pt-2">
          <label className="inline-flex items-center gap-1.5 text-[13px]">
            <input type="checkbox" checked={v.cumple_tecnica} onChange={(e) => setV({ ...v, cumple_tecnica: e.target.checked })} />
            Cumple técnica
          </label>
          <label className="inline-flex items-center gap-1.5 text-[13px]">
            <input type="checkbox" checked={v.cumple_legal} onChange={(e) => setV({ ...v, cumple_legal: e.target.checked })} />
            Cumple legal
          </label>
        </div>
      </form>
    </Modal>
  );
}

function FalloModal({ licitID, participantes, open, onClose, onDone }: any) {
  const [ganador, setGanador] = useState('');
  const [monto, setMonto] = useState(0);
  const [dictamen, setDictamen] = useState('');
  const [fallo, setFallo] = useState('');
  const [desierta, setDesierta] = useState(false);
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/licitaciones/${licitID}/fallo`, {
        method: 'POST',
        body: desierta
          ? { dictamen, fallo, es_desierta: true }
          : { dictamen, fallo, proveedor_ganador_id: ganador, monto_adjudicado: Number(monto) },
      });
      onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Emitir fallo" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="fallo-form" loading={subm}>Emitir fallo</Button>
      </>}>
      <form id="fallo-form" onSubmit={submit} className="flex flex-col gap-3">
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={desierta} onChange={(e) => setDesierta(e.target.checked)} />
          Declarar desierta
        </label>
        {!desierta && (
          <>
            <div>
              <label className="text-[13px] font-medium">Proveedor ganador</label>
              <select required value={ganador} onChange={(e) => {
                setGanador(e.target.value);
                const p = participantes.find((x: any) => x.proveedor_id === e.target.value);
                if (p?.monto_economico) setMonto(p.monto_economico);
              }}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
                <option value="">(selecciona)</option>
                {participantes.filter((p: any) => p.propuesta_id).map((p: any) => (
                  <option key={p.proveedor_id} value={p.proveedor_id}>
                    {p.proveedor_nombre} — {fmt.money(p.monto_economico ?? 0)}
                  </option>
                ))}
              </select>
            </div>
            <Input name="monto" label="Monto adjudicado" type="number" step="0.01" required
              value={monto} onChange={(e) => setMonto(parseFloat(e.target.value) || 0)} />
          </>
        )}
        <div>
          <label className="text-[13px] font-medium">Dictamen técnico</label>
          <textarea rows={2} value={dictamen} onChange={(e) => setDictamen(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
        <div>
          <label className="text-[13px] font-medium">Fallo</label>
          <textarea rows={2} value={fallo} onChange={(e) => setFallo(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
      </form>
    </Modal>
  );
}
